"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { VlitePlayerType } from "@/scott/ui/elements/video";
import {
  videoCompleteToDataLayer,
  videoProgressToDataLayer,
  videoStartToDataLayer,
} from "@/utils/gtm.utils";

const QUARTER_PROGRESS = 25;
const HALF_PROGRESS = 50;
const THREE_QUARTER_PROGRESS = 75;
const VIDEO_PROGRESS_MILESTONES = [
  QUARTER_PROGRESS,
  HALF_PROGRESS,
  THREE_QUARTER_PROGRESS,
] as const;
const PERCENT_MULTIPLIER = 100;

function getVideoTitle(src: string) {
  try {
    const url = new URL(src);
    const segments = url.pathname.split("/").filter(Boolean);
    const lastSegment = segments.at(-1) ?? "video";
    return decodeURIComponent(lastSegment);
  } catch {
    return src;
  }
}

type UseGtmVideoTrackingParams = {
  src: string;
  isBackgroundVideo?: boolean;
};

export function useGtmVideoTracking({
  src,
  isBackgroundVideo,
}: Readonly<UseGtmVideoTrackingParams>) {
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const firedMilestonesRef = useRef<Record<number, boolean>>({});
  const videoDurationRef = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  const videoTitle = useMemo(() => getVideoTitle(src), [src]);

  const onPlay = useCallback(
    (_event: Event) => {
      if (isBackgroundVideo) {
        return;
      }

      if (!startedRef.current) {
        videoStartToDataLayer(videoTitle, src);
        startedRef.current = true;
      }
    },
    [isBackgroundVideo, src, videoTitle]
  );

  const onEnded = useCallback(
    (_event: Event) => {
      if (isBackgroundVideo) {
        return;
      }

      if (!completedRef.current) {
        videoCompleteToDataLayer(videoTitle, src, videoDurationRef.current);
        completedRef.current = true;
      }
    },
    [isBackgroundVideo, src, videoTitle]
  );

  const onReady = useCallback(
    (player: VlitePlayerType) => {
      cleanupRef.current?.();

      if (isBackgroundVideo) {
        return;
      }

      const media = player.media;

      if (!(media instanceof HTMLVideoElement)) {
        return;
      }

      const onTimeUpdate = () => {
        const duration = media.duration;
        if (!Number.isFinite(duration) || duration <= 0) {
          return;
        }

        videoDurationRef.current = duration;
        const progressPercent = Math.floor(
          (media.currentTime / duration) * PERCENT_MULTIPLIER
        );

        for (const milestone of VIDEO_PROGRESS_MILESTONES) {
          if (
            progressPercent >= milestone &&
            !firedMilestonesRef.current[milestone]
          ) {
            videoProgressToDataLayer(
              videoTitle,
              src,
              milestone,
              media.currentTime,
              duration
            );
            firedMilestonesRef.current[milestone] = true;
          }
        }
      };

      media.addEventListener("timeupdate", onTimeUpdate);
      cleanupRef.current = () => {
        media.removeEventListener("timeupdate", onTimeUpdate);
      };
    },
    [isBackgroundVideo, src, videoTitle]
  );

  useEffect(() => {
    startedRef.current = false;
    completedRef.current = false;
    firedMilestonesRef.current = {};
    videoDurationRef.current = 0;
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  useEffect(
    () => () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    },
    []
  );

  return { onPlay, onEnded, onReady };
}
